/**
 * 좌 레일 — 설정 (DESIGN §8.6.8).
 *
 * 네 구획을 위에서 아래로 쌓는다:
 *   1 정의   고장 정의 3분할. 필터가 아니라 **모든 숫자의 단위**다
 *   2 페이지 탭 7개. 질문의 순서(§8)를 세로로 세운다
 *   3 필터   트리거만. 목록은 레일 밖 플라이아웃 (§8.6.1 이 관찰한 실패의 답)
 *   4 범위   이 화면이 실제로 타는 축 / 타지 않는 축과 사유
 *
 * **레일이 길어지지 않는 이유**가 이 구조의 전부다. v0.3 이 좌 레일을 폐기한
 * 원인은 "선택지를 펼치면 레일이 무한정 길어진다" 였다 — 사업자 146 · 제조사
 * 44 · 시군구 다수. 여기서는 레일이 트리거만 갖고 목록은 레일 밖에 뜬다.
 */

import { ModeToggle } from '@/components/ModeToggle'
import { FilterBar } from '@/components/FilterBar'
import { AXIS_LABEL, ignoredAxes, scopeOf } from '@/lib/pageScope'
import './Rail.css'

export interface RailPage {
  key: string
  title: string
  lead: string
  /** 대분류 (DESIGN §8.0). 근거 구획은 'base' */
  group: string
}

export interface RailGroup {
  key: string
  title: string
  lead: string
}

interface Props {
  pages: readonly RailPage[]
  groups: readonly RailGroup[]
  page: string
}

export function Rail({ pages, groups, page }: Props) {
  return (
    <aside className="rail" aria-label="화면 설정">
      {/* 1 정의 — ModeToggle 이 스스로 "고장 정의" 라벨을 갖는다 */}
      <div className="rail__sec rail__sec--def">
        <ModeToggle />
      </div>

{/* 2 페이지 — 두 대분류 + 근거 (§8.0). 비활성 탭의 리드는 접는다 */}
      <nav className="rail__sec rail__nav" aria-label="페이지">
        {groups.map((g) => {
          const mine = pages.filter((p) => p.group === g.key)
          if (mine.length === 0) return null
          const labelId = `rail-group-${g.key}`
          return (
            <div className="rail__group" key={g.key}>
              <p className="rail__grouphead" id={labelId}>
                <span className="rail__grouptitle">{g.title}</span>
                {g.lead && <span className="rail__grouplead">{g.lead}</span>}
              </p>
              <ul className="rail__pagelist" aria-labelledby={labelId}>
                {mine.map((p) => {
                  const on = p.key === page
                  // 번호는 전체 목록 기준이다 — 대분류 안에서 다시 1부터 세면
                  // 「3번 화면」이 두 개가 되어 서로를 가리킬 수 없다
                  const n = pages.findIndex((x) => x.key === p.key) + 1
                  return (
                    <li key={p.key}>
                      <a
                        href={`#/${p.key}`}
                        className={`rail__page${on ? ' is-on' : ''}`}
                        aria-current={on ? 'page' : undefined}
                      >
                        <span className="rail__pagenum num" aria-hidden="true">
                          {n}
                        </span>
                        <span className="rail__pagetext">
                          <span className="rail__pagename">{p.title}</span>
                          {on && <span className="rail__pagelead">{p.lead}</span>}
                        </span>
                      </a>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </nav>

      {/* 3 필터 — 이 화면에서 무엇이 안 걸리는지를 필터 자리에서 말한다 (§8.6.10).
          .fbar 루트 클래스와 트리거 마크업은 바꾸지 않는다 — e2e 가 잡고 있다 */}
      <div className="rail__sec">
        <FilterBar page={page} />
      </div>

      {/* 4 범위 */}
      <ScopeNotice page={page} />
    </aside>
  )
}

/**
 * 이 화면이 타지 않는 필터 축을 **숨기지 않고 사유와 함께** 적는다.
 * 지도 레이어 목록(§8.2.1 규칙 3)과 같은 문법이다 — 못 하는 것을 지우면
 * 사용자는 자기가 뭘 잘못했는지 묻게 된다.
 */
function ScopeNotice({ page }: { page: string }) {
  const scope = scopeOf(page)
  const ignored = ignoredAxes(page)

  if (ignored.length === 0) {
    return (
      <div className="rail__sec rail__scope">
        <p className="rail__legend">이 화면의 범위</p>
        <p className="rail__scopeall">왼쪽 필터가 모두 적용됩니다.</p>
      </div>
    )
  }

  return (
    <div className="rail__sec rail__scope">
      <p className="rail__legend">이 화면의 범위</p>
      <p className="rail__scopewhy">{scope.reason}</p>
      <ul className="rail__scopelist">
        {ignored.map((a) => (
          <li key={a} className="rail__scopeoff">
            <span className="rail__scopedot hatch-na" aria-hidden="true" />
            {AXIS_LABEL[a]}
            <span className="rail__scopetag">적용 안 됨</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
